/* ============================================
   SLIDE DECK VIEWER COMPONENT
   The Creative Orchestra - GEMINI Framework

   Canvas-free presentation viewer: each slide is rasterised
   once, cached as an image, and displayed as an <img>. This
   keeps navigation instant (neighbouring slides are warmed in
   the background) and means a re-render can never blank the
   slide, which a live <canvas> would.

   The PDF itself is never offered for download.
   ============================================ */

import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minimize2,
  Pause,
  Play,
  Timer,
  X,
  Presentation,
  Loader2,
} from "lucide-react";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const PDF_OPTIONS = {
  cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/cmaps/`,
  cMapPacked: true,
  standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/standard_fonts/`,
};

/** Slides warmed ahead of / behind the current one. */
const PREFETCH_AHEAD = 3;
const PREFETCH_BEHIND = 1;
const CACHE_LIMIT = 48;
const MAX_RENDER_PX = 2200;

/* Auto-advance timing, in seconds */
const DEFAULT_INTERVAL = 5;
const MIN_INTERVAL = 1;
const MAX_INTERVAL = 120;

/* ------------------------------------------------
   Shared slide cache and render queue
   ------------------------------------------------ */

const slideCache = new Map();

const cacheKey = (url, page, widthPx) => `${url}|${page}@${widthPx}`;

const evictCache = (protectedKeys) => {
  for (const key of slideCache.keys()) {
    if (slideCache.size <= CACHE_LIMIT) break;
    if (protectedKeys.has(key)) continue;
    URL.revokeObjectURL(slideCache.get(key));
    slideCache.delete(key);
  }
};

const renderQueue = [];
let activeRenders = 0;
const MAX_PARALLEL_RENDERS = 2;

const runQueue = () => {
  while (activeRenders < MAX_PARALLEL_RENDERS && renderQueue.length) {
    const job = renderQueue.shift();
    activeRenders += 1;
    Promise.resolve()
      .then(job.work)
      .then(job.resolve, job.reject)
      .finally(() => {
        activeRenders -= 1;
        runQueue();
      });
  }
};

const enqueue = (work, urgent) =>
  new Promise((resolve, reject) => {
    const job = { work, resolve, reject };
    if (urgent) renderQueue.unshift(job);
    else renderQueue.push(job);
    runQueue();
  });

/** Quantised so small resizes reuse cached slides. */
const renderWidthFor = (cssWidth) => {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const raw = Math.max(640, cssWidth) * dpr;
  return Math.min(MAX_RENDER_PX, Math.ceil(raw / 200) * 200);
};

const renderSlide = (pdf, url, page, widthPx, urgent) => {
  const key = cacheKey(url, page, widthPx);
  const cached = slideCache.get(key);
  if (cached) {
    slideCache.delete(key);
    slideCache.set(key, cached);
    return Promise.resolve(cached);
  }

  return enqueue(async () => {
    const hit = slideCache.get(key);
    if (hit) return hit;

    const pdfPage = await pdf.getPage(page);
    const unscaled = pdfPage.getViewport({ scale: 1 });
    const viewport = pdfPage.getViewport({ scale: widthPx / unscaled.width });

    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.floor(viewport.width));
    canvas.height = Math.max(1, Math.floor(viewport.height));
    const ctx = canvas.getContext("2d", { alpha: false });
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await pdfPage.render({ canvasContext: ctx, viewport }).promise;

    const objectUrl = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) =>
          blob
            ? resolve(URL.createObjectURL(blob))
            : reject(new Error("Slide render failed")),
        "image/jpeg",
        0.9,
      );
    });

    slideCache.set(key, objectUrl);
    return objectUrl;
  }, urgent);
};

/* ------------------------------------------------
   Presentation chrome (module scope so the slide
   image is never remounted by a parent re-render)
   ------------------------------------------------ */

const DeckChrome = ({
  fullscreen,
  title,
  slideSrc,
  currentPage,
  numPages,
  isLoading,
  isPending,
  error,
  stageRef,
  isPlaying,
  intervalSec,
  onTogglePlay,
  onIntervalChange,
  onPrev,
  onNext,
  onJump,
  onToggleFullscreen,
  onClose,
}) => (
  <div className={`flex flex-col ${fullscreen ? "h-full" : ""}`}>
    {/* Top bar */}
    <div
      className={`flex items-center justify-between px-4 py-3 ${
        fullscreen
          ? "bg-black/80 backdrop-blur-sm"
          : "bg-white/[0.03] border-b border-white/10"
      }`}
    >
      <div className="flex items-center gap-3">
        <Presentation
          className={`w-5 h-5 ${fullscreen ? "text-white/80" : "text-indigo-400"}`}
        />
        <span className="font-medium text-sm text-white">{title}</span>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-400 font-mono mr-2">
          {currentPage} / {numPages || "—"}
        </span>

        <motion.button
          onClick={onToggleFullscreen}
          className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          title={fullscreen ? "Exit Fullscreen (Esc)" : "Fullscreen"}
        >
          {fullscreen ? (
            <Minimize2 className="w-5 h-5" />
          ) : (
            <Maximize2 className="w-5 h-5" />
          )}
        </motion.button>

        {fullscreen && (
          <motion.button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-red-500/20 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="Close (Esc)"
          >
            <X className="w-5 h-5" />
          </motion.button>
        )}
      </div>
    </div>

    {/* Slide stage */}
    <div
      ref={stageRef}
      className={`relative flex items-center justify-center bg-black/40 ${
        fullscreen ? "flex-1 min-h-0" : ""
      }`}
      style={!fullscreen ? { minHeight: "450px" } : undefined}
    >
      {error ? (
        <div className="flex flex-col items-center gap-3 text-gray-400 p-8 text-center">
          <Presentation className="w-10 h-10 text-gray-600" />
          <span className="text-sm">{error}</span>
        </div>
      ) : (
        <>
          {slideSrc && (
            <img
              src={slideSrc}
              alt={`${title} — slide ${currentPage}`}
              draggable={false}
              className="max-w-full max-h-full object-contain select-none"
              style={{ pointerEvents: "none" }}
            />
          )}

          {(isLoading || (!slideSrc && !error)) && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-gray-400">
              <Loader2 className="w-8 h-8 animate-spin" />
              <span className="text-sm">
                {isLoading ? "Loading presentation…" : "Preparing slide…"}
              </span>
            </div>
          )}

          {/* Subtle indicator while the next slide is still rasterising */}
          {isPending && slideSrc && (
            <div className="absolute top-3 right-3 p-1.5 rounded-full bg-black/60 backdrop-blur-sm">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-white/70" />
            </div>
          )}

          {/* Countdown to the next slide while playing */}
          {isPlaying && slideSrc && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
              <motion.div
                key={`${currentPage}-${intervalSec}`}
                className="h-full bg-indigo-400"
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: intervalSec, ease: "linear" }}
              />
            </div>
          )}

          {/* Click zones */}
          <button
            onClick={onPrev}
            disabled={currentPage <= 1}
            className="absolute left-0 top-0 bottom-0 w-1/4 cursor-pointer opacity-0 hover:opacity-100
              transition-opacity flex items-center justify-start pl-2 disabled:cursor-default"
            aria-label="Previous slide"
          >
            <div className="p-2 rounded-full bg-black/50 backdrop-blur-sm text-white/80">
              <ChevronLeft className="w-6 h-6" />
            </div>
          </button>
          <button
            onClick={onNext}
            disabled={numPages > 0 && currentPage >= numPages}
            className="absolute right-0 top-0 bottom-0 w-1/4 cursor-pointer opacity-0 hover:opacity-100
              transition-opacity flex items-center justify-end pr-2 disabled:cursor-default"
            aria-label="Next slide"
          >
            <div className="p-2 rounded-full bg-black/50 backdrop-blur-sm text-white/80">
              <ChevronRight className="w-6 h-6" />
            </div>
          </button>
        </>
      )}
    </div>

    {/* Bottom navigation */}
    <div
      className={`flex items-center justify-center gap-4 px-4 py-3 ${
        fullscreen
          ? "bg-black/80 backdrop-blur-sm"
          : "bg-white/[0.03] border-t border-white/10"
      }`}
    >
      <motion.button
        onClick={onPrev}
        disabled={currentPage <= 1}
        className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium
          bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white
          disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        whileHover={currentPage > 1 ? { scale: 1.02 } : {}}
        whileTap={currentPage > 1 ? { scale: 0.98 } : {}}
      >
        <ChevronLeft className="w-4 h-4" />
        Previous
      </motion.button>

      <div className="flex items-center gap-2">
        <input
          type="number"
          min={1}
          max={numPages || 1}
          value={currentPage}
          onChange={(e) => onJump(parseInt(e.target.value, 10))}
          aria-label="Go to slide"
          className="w-14 text-center text-sm font-mono bg-white/5 border border-white/10
            rounded-lg px-2 py-1.5 text-white focus:outline-none focus:border-indigo-500
            [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none
            [&::-webkit-inner-spin-button]:appearance-none"
        />
        <span className="text-sm text-gray-500">of {numPages || "—"}</span>
      </div>

      <motion.button
        onClick={onNext}
        disabled={numPages > 0 && currentPage >= numPages}
        className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium
          bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white
          disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        Next
        <ChevronRight className="w-4 h-4" />
      </motion.button>

      {/* Auto-advance */}
      <div className="flex items-center gap-2 pl-2 ml-1 border-l border-white/10">
        <motion.button
          onClick={onTogglePlay}
          disabled={!numPages}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium
            transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
              isPlaying
                ? "bg-indigo-500/30 text-white hover:bg-indigo-500/40"
                : "bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white"
            }`}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          title={
            isPlaying
              ? "Pause the slideshow"
              : currentPage >= numPages && numPages > 0
                ? "Play from the first slide"
                : "Play the slides automatically"
          }
        >
          {isPlaying ? (
            <Pause className="w-4 h-4" />
          ) : (
            <Play className="w-4 h-4" />
          )}
          {isPlaying ? "Pause" : "Auto play"}
        </motion.button>

        <div
          className="flex items-center gap-1.5 text-gray-500"
          title="Seconds to hold each slide"
        >
          <Timer className="w-4 h-4" />
          <input
            type="number"
            min={MIN_INTERVAL}
            max={MAX_INTERVAL}
            step={1}
            value={intervalSec}
            onChange={(e) => onIntervalChange(e.target.value)}
            aria-label="Seconds per slide"
            className="w-14 text-center text-sm font-mono bg-white/5 border border-white/10
              rounded-lg px-2 py-1.5 text-white focus:outline-none focus:border-indigo-500
              [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none
              [&::-webkit-inner-spin-button]:appearance-none"
          />
          <span className="text-sm">sec</span>
        </div>
      </div>
    </div>

    {!fullscreen && (
      <p className="text-center text-xs text-gray-600 py-3">
        Use arrow keys to navigate slides · P to start or pause auto play
      </p>
    )}
  </div>
);

/* ------------------------------------------------
   Viewer
   ------------------------------------------------ */

const SlideDeckViewer = ({ url, title = "Presentation", className = "" }) => {
  const stageRef = useRef(null);
  const pdfRef = useRef(null);

  // Keyed by url so a new deck reports loading without an extra effect
  const [doc, setDoc] = useState({ url: null, numPages: 0, error: null });
  const [currentPage, setCurrentPage] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isHot, setIsHot] = useState(false);
  const [stageWidth, setStageWidth] = useState(900);
  const [slides, setSlides] = useState({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [intervalSec, setIntervalSec] = useState(DEFAULT_INTERVAL);

  const widthPx = useMemo(() => renderWidthFor(stageWidth), [stageWidth]);

  const isLoading = doc.url !== url;
  const numPages = isLoading ? 0 : doc.numPages;
  const error = isLoading ? null : doc.error;

  /* ---- load the document ---- */
  useEffect(() => {
    let cancelled = false;

    const task = pdfjsLib.getDocument({ url, ...PDF_OPTIONS });
    task.promise.then(
      (pdf) => {
        if (cancelled) return;
        pdfRef.current = pdf;
        setDoc({ url, numPages: pdf.numPages, error: null });
      },
      (err) => {
        if (cancelled) return;
        console.error("Presentation load failed:", err);
        setDoc({
          url,
          numPages: 0,
          error: "Failed to load the presentation.",
        });
      },
    );

    return () => {
      cancelled = true;
      task.destroy?.();
    };
  }, [url]);

  /* ---- track the stage width ---- */
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const update = () =>
      setStageWidth((prev) => {
        const next = el.clientWidth;
        return next > 0 && next !== prev ? next : prev;
      });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isFullscreen, isLoading]);

  /* ---- rasterise the current slide, then warm its neighbours ---- */
  useEffect(() => {
    const pdf = pdfRef.current;
    if (!pdf || isLoading || !numPages) return undefined;

    let cancelled = false;

    const wanted = [currentPage];
    for (let d = 1; d <= PREFETCH_AHEAD; d += 1) {
      if (currentPage + d <= numPages) wanted.push(currentPage + d);
    }
    for (let d = 1; d <= PREFETCH_BEHIND; d += 1) {
      if (currentPage - d >= 1) wanted.push(currentPage - d);
    }

    const load = async () => {
      for (const [i, page] of wanted.entries()) {
        if (cancelled) return;
        try {
          const src = await renderSlide(pdf, url, page, widthPx, i === 0);
          if (cancelled) return;
          setSlides((prev) =>
            prev[`${page}@${widthPx}`] === src
              ? prev
              : { ...prev, [`${page}@${widthPx}`]: src },
          );
        } catch (err) {
          if (!cancelled && i === 0) {
            console.error(`Slide ${page} failed to render:`, err);
          }
        }
      }
      if (!cancelled) {
        evictCache(new Set(wanted.map((p) => cacheKey(url, p, widthPx))));
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [currentPage, numPages, isLoading, widthPx, url]);

  /* ---- navigation ---- */
  const goPrev = useCallback(() => {
    setCurrentPage((p) => Math.max(p - 1, 1));
  }, []);

  const goNext = useCallback(() => {
    setCurrentPage((p) => (numPages ? Math.min(p + 1, numPages) : p));
  }, [numPages]);

  const jumpTo = useCallback(
    (value) => {
      if (!Number.isFinite(value) || !numPages) return;
      setCurrentPage(Math.max(1, Math.min(value, numPages)));
    },
    [numPages],
  );

  /* ---- auto-advance ---- */
  const togglePlay = useCallback(() => {
    if (!numPages) return;
    setIsPlaying((playing) => {
      if (playing) return false;
      // Pressing play on the last slide starts the deck over
      if (currentPage >= numPages) setCurrentPage(1);
      return true;
    });
  }, [numPages, currentPage]);

  const changeInterval = useCallback((raw) => {
    const value = parseFloat(raw);
    if (!Number.isFinite(value)) return;
    setIntervalSec(Math.min(MAX_INTERVAL, Math.max(MIN_INTERVAL, value)));
  }, []);

  // Waits for the slide to be on screen before timing it, so a slow
  // render never causes a slide to be skipped past.
  const currentReady = Boolean(slides[`${currentPage}@${widthPx}`]);

  useEffect(() => {
    if (!isPlaying || !numPages || !currentReady) return undefined;

    if (currentPage >= numPages) {
      const stop = window.setTimeout(
        () => setIsPlaying(false),
        intervalSec * 1000,
      );
      return () => window.clearTimeout(stop);
    }

    const timer = window.setTimeout(
      () => setCurrentPage((p) => Math.min(p + 1, numPages)),
      intervalSec * 1000,
    );
    return () => window.clearTimeout(timer);
  }, [isPlaying, currentPage, numPages, intervalSec, currentReady]);

  // Leaving the deck entirely should not keep it advancing forever
  useEffect(() => {
    if (!isPlaying) return undefined;
    const onVisibility = () => {
      if (document.hidden) setIsPlaying(false);
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [isPlaying]);

  /* ---- keyboard: only the hovered or fullscreen deck responds ---- */
  useEffect(() => {
    if (!isHot && !isFullscreen) return undefined;

    const onKey = (e) => {
      if (e.target?.tagName === "INPUT") return;
      switch (e.key) {
        case "ArrowRight":
        case "ArrowDown":
        case "PageDown":
        case " ":
          e.preventDefault();
          goNext();
          break;
        case "ArrowLeft":
        case "ArrowUp":
        case "PageUp":
          e.preventDefault();
          goPrev();
          break;
        case "Home":
          e.preventDefault();
          jumpTo(1);
          break;
        case "End":
          e.preventDefault();
          jumpTo(numPages);
          break;
        case "Escape":
          if (isFullscreen) setIsFullscreen(false);
          break;
        case "p":
        case "P":
          e.preventDefault();
          togglePlay();
          break;
        default:
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isHot, isFullscreen, goNext, goPrev, jumpTo, numPages, togglePlay]);

  /* ---- lock scroll behind the fullscreen overlay ---- */
  useEffect(() => {
    if (!isFullscreen) return undefined;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isFullscreen]);

  const preventContextMenu = useCallback((e) => e.preventDefault(), []);

  // Fall back to the previous resolution of the same slide while a
  // larger one renders, so the stage never goes blank.
  const slideSrc =
    slides[`${currentPage}@${widthPx}`] ??
    Object.entries(slides).find(([k]) => k.startsWith(`${currentPage}@`))?.[1];

  const chrome = (
    <DeckChrome
      fullscreen={isFullscreen}
      title={title}
      slideSrc={slideSrc}
      currentPage={currentPage}
      numPages={numPages}
      isLoading={isLoading}
      isPending={!currentReady}
      error={error}
      stageRef={stageRef}
      isPlaying={isPlaying}
      intervalSec={intervalSec}
      onTogglePlay={togglePlay}
      onIntervalChange={changeInterval}
      onPrev={goPrev}
      onNext={goNext}
      onJump={jumpTo}
      onToggleFullscreen={() => setIsFullscreen((v) => !v)}
      onClose={() => setIsFullscreen(false)}
    />
  );

  return (
    <AnimatePresence mode="wait">
      {isFullscreen ? (
        <motion.div
          key="fullscreen"
          className="fixed inset-0 z-[100] bg-black flex flex-col"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onContextMenu={preventContextMenu}
        >
          {chrome}
        </motion.div>
      ) : (
        <div
          key="embedded"
          className={`rounded-2xl overflow-hidden border border-white/10 ${className}`}
          onContextMenu={preventContextMenu}
          onDragStart={preventContextMenu}
          onMouseEnter={() => setIsHot(true)}
          onMouseLeave={() => setIsHot(false)}
        >
          {chrome}
        </div>
      )}
    </AnimatePresence>
  );
};

export default SlideDeckViewer;
